import { useRouter } from 'next/router'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { OverlayContext } from 'services/contexts'
import { useElement } from 'services/hooks'
import usePreventScroll from 'services/hooks/usePreventScroll'

import CloseButton from './CloseButton'

interface IModalOverlayProps {
  allowHeaderClicks?: boolean
  allowHeaderClicksMobile?: boolean
  allowHeaderClicksTablet?: boolean
  opacity?: number
}
export const ModalOverlay = styled.div<IModalOverlayProps>`
  position: fixed;
  top: ${props => (props.allowHeaderClicksMobile ? props.theme.metrics.headerHeightPhone : 0)}px;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${props => props.theme.zIndices.level11};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    top: ${props => (props.allowHeaderClicksTablet ? props.theme.metrics.headerHeightTablet : 0)}px;
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    top: ${props => (props.allowHeaderClicks ? props.theme.metrics.headerHeight : 0)}px;
  }

  opacity: ${props => props.opacity || 0};
  transition: opacity 150ms ease;
`

interface IModalCardProps {
  borderRadius?: number
  flexBasis?: number
  headerHidden?: boolean
  maxHeight?: string
  maxWidth?: number
  margin?: string
  marginHorizontal?: string
  padding?: string
}
export const ModalCard = styled.div<IModalCardProps>`
  position: relative;
  margin: calc(10px + ${props => props.theme.metrics.headerHeightPhone}px) 10px 10px;
  ${props => props.headerHidden && 'margin: 10px 10px;'}
  ${props => props.margin && `margin: ${props.margin};`}
  ${props => props.padding && `padding: ${props.padding};`}
  ${props =>
    props.marginHorizontal
    && `
    margin-left: ${props.marginHorizontal};
    margin-right: ${props.marginHorizontal};
  `}
  max-height: ${props => props.maxHeight || '80vh'};
  ${props => props.maxWidth && `max-width: ${props.maxWidth}px;`}
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex-basis: ${props => (Number.isInteger(props.flexBasis) ? props.flexBasis : 900)}px;
  background: #ffffff;
  border-radius: ${props => (Number.isInteger(props.borderRadius) ? props.borderRadius : 8)}px;
  box-shadow: 0px 4px 20px ${props => props.theme.colors.transparentBlackAlpha10};

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    margin: calc(10vh + ${props => props.theme.metrics.headerHeight}px) 5vw 10vh;
    ${props => props.headerHidden && 'margin: 10vh 5vw;'}
    ${props => props.margin && `margin: ${props.margin};`}
  }
`

interface IScrollBodyProps {
  unsetScroll?: boolean
}
const ScrollBody = styled.div<IScrollBodyProps>`
  height: 100%;
  width: 100%;

  overflow: auto; /* fixes default scroll shown on windows */
  ${props => props.unsetScroll && 'overflow: unset;'}/* for HelloSign embed Edge browser compat */
`

interface IProps {
  allowHeaderClicks?: boolean
  allowHeaderClicksMobile?: boolean
  allowHeaderClicksTablet?: boolean
  borderRadius?: number
  closeButton?: boolean
  closeButtonHideMobile?: boolean
  closeButtonHideTablet?: boolean
  closeButtonColor?: string
  closeButtonFixed?: boolean // ignores padding and sets close button position on top of the modal content
  closeButtonPadding?: string
  closeButtonTopPosition?: number
  closeButtonRightPosition?: number
  closeText?: boolean
  closeTextUpperCase?: boolean
  closeTextFontWeight?: number
  closeTextLineHeight?: string
  closeTextFontSize?: string
  fixedOverlay?: boolean
  flexBasis?: number
  maxHeight?: string
  margin?: string
  marginHorizontal?: string
  maxWidth?: number
  noOverlay?: boolean
  padding?: string
  unsetScroll?: boolean
  visible: boolean
  setVisible?(visible: boolean): void
  onClose?: () => void
}

const Modal: FunctionComponent<IProps> = ({
  allowHeaderClicks,
  allowHeaderClicksMobile,
  allowHeaderClicksTablet,
  borderRadius,
  children,
  closeButton,
  closeButtonHideMobile,
  closeButtonHideTablet,
  closeButtonColor,
  closeButtonFixed,
  closeButtonPadding,
  closeButtonTopPosition,
  closeButtonRightPosition,
  closeText,
  closeTextFontSize,
  closeTextLineHeight,
  closeTextUpperCase,
  closeTextFontWeight,
  fixedOverlay,
  flexBasis,
  margin,
  marginHorizontal,
  maxHeight,
  maxWidth,
  noOverlay,
  padding,
  unsetScroll,
  visible,
  setVisible,
  onClose,
}) => {
  const { showOverlay, hideOverlay, overlay } = useContext(OverlayContext)

  const [modalCardRef, modalCardElement] = useElement<HTMLDivElement>()
  const { reenableScroll } = usePreventScroll(overlay, modalCardElement, 'modal')

  const router = useRouter()
  const isInListingFlow = router.pathname.split('/').includes('create')

  const closeModal = () => {
    /**
     * NB: Order is important here. `reenableScroll` relies on a ref, and `setVisible` can cause
     * the modal to unmount, which would clear the ref, resulting in persistent scroll lock.
     */
    reenableScroll()
    hideOverlay()
    if (onClose) onClose()

    if (setVisible) setVisible(false)
  }

  // make sure we disable the scroll lock if a parent component unmounts the modal
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => reenableScroll()
  }, [])

  const [opacity, setOpacity] = useState(0)
  useEffect(() => {
    if (visible && noOverlay) return

    if (visible) {
      showOverlay()
      setOpacity(1)
    } else {
      hideOverlay()
    }
  }, [visible])

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    // inside click
    if (modalCardRef?.current?.contains(target)) return

    // outside click
    if (setVisible) closeModal() // sometimes modal should not be hidden on outside click
  }

  useEffect(() => {
    if (fixedOverlay) return // for screens where overlay/modal is fixed

    if (visible) document.addEventListener('mousedown', handleOutsideClick)
    else document.removeEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [visible])

  if (!visible) return null

  return (
    <ModalOverlay
      allowHeaderClicks={allowHeaderClicks}
      allowHeaderClicksMobile={allowHeaderClicksMobile}
      allowHeaderClicksTablet={allowHeaderClicksTablet}
      opacity={opacity}
    >
      <ModalCard
        ref={modalCardRef}
        borderRadius={borderRadius}
        flexBasis={flexBasis}
        headerHidden={isInListingFlow}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        margin={margin}
        marginHorizontal={marginHorizontal}
        padding={padding}
      >
        {closeButton && (
          <CloseButton
            closeButtonHideMobile={closeButtonHideMobile}
            closeButtonHideTablet={closeButtonHideTablet}
            closeButtonColor={closeButtonColor}
            closeButtonFixed={closeButtonFixed}
            closeButtonPadding={closeButtonPadding}
            closeButtonTopPosition={closeButtonTopPosition}
            closeButtonRightPosition={closeButtonRightPosition}
            closeText={closeText}
            closeTextFontSize={closeTextFontSize}
            closeTextLineHeight={closeTextLineHeight}
            closeTextUpperCase={closeTextUpperCase}
            closeTextFontWeight={closeTextFontWeight}
            defaultPadding={!padding}
            onClick={closeModal}
          />
        )}
        <ScrollBody unsetScroll={unsetScroll}>{children}</ScrollBody>
      </ModalCard>
    </ModalOverlay>
  )
}

export default Modal
